import { useState, useEffect } from 'react'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  WalletIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { supabase } from '@/lib/supabase'

interface Wallet {
  id: string
  owner_id: string
  owner_type: 'user' | 'operator'
  owner_name: string
  owner_email: string
  wallet_name: string
  wallet_address: string
  channel_code: string
  channel_name: string
  channel_icon: string
  is_default: boolean
  is_enabled: boolean
  account_holder_name?: string
  bank_name?: string
  bank_branch?: string
  network?: string
  created_at: string
}

export default function WalletsManagementPage() {
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<'all' | 'user' | 'operator'>('all')
  const [channelFilter, setChannelFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const limit = 20

  const fetchWallets = async () => {
    try {
      setLoading(true)
      
      let query = supabase
        .from('user_wallets')
        .select(`
          id,
          owner_id,
          owner_type,
          wallet_name,
          wallet_address,
          channel_code,
          is_default,
          is_enabled,
          account_holder_name,
          bank_name,
          bank_branch,
          network,
          created_at,
          payment_channels(channel_name, channel_icon)
        `, { count: 'exact' })

      // 应用筛选条件
      if (ownerTypeFilter !== 'all') {
        query = query.eq('owner_type', ownerTypeFilter)
      }

      if (channelFilter !== 'all') {
        query = query.eq('channel_code', channelFilter)
      }

      // 分页
      const offset = (currentPage - 1) * limit
      query = query.range(offset, offset + limit - 1).order('created_at', { ascending: false })

      const { data, error, count } = await query

      if (error) throw error

      if (data) {
        // 获取所有者信息
        const userIds = data.filter(w => w.owner_type === 'user').map(w => w.owner_id)
        const operatorIds = data.filter(w => w.owner_type === 'operator').map(w => w.owner_id)

        let usersMap = new Map()
        let operatorsMap = new Map()

        // 获取用户信息
        if (userIds.length > 0) {
          const { data: users } = await supabase
            .from('users')
            .select('id, full_name, email')
            .in('id', userIds)
          
          users?.forEach(u => usersMap.set(u.id, u))
        }

        // 获取运营商信息（通过 admin_users）
        if (operatorIds.length > 0) {
          const { data: adminUsers } = await supabase
            .from('admin_users')
            .select('id, full_name, email')
            .in('id', operatorIds)
          
          adminUsers?.forEach(au => operatorsMap.set(au.id, au))
        }

        // 组合数据
        const walletsWithOwners = data.map((wallet: any) => {
          const owner = wallet.owner_type === 'user' 
            ? usersMap.get(wallet.owner_id)
            : operatorsMap.get(wallet.owner_id)

          return {
            id: wallet.id,
            owner_id: wallet.owner_id,
            owner_type: wallet.owner_type,
            owner_name: owner?.full_name || '未知',
            owner_email: owner?.email || '',
            wallet_name: wallet.wallet_name,
            wallet_address: wallet.wallet_address,
            channel_code: wallet.channel_code,
            channel_name: wallet.payment_channels?.channel_name || '',
            channel_icon: wallet.payment_channels?.channel_icon || '',
            is_default: wallet.is_default,
            is_enabled: wallet.is_enabled,
            account_holder_name: wallet.account_holder_name,
            bank_name: wallet.bank_name,
            bank_branch: wallet.bank_branch,
            network: wallet.network,
            created_at: wallet.created_at,
          }
        })

        // 应用搜索过滤
        let filteredWallets = walletsWithOwners
        if (searchTerm) {
          const term = searchTerm.toLowerCase()
          filteredWallets = walletsWithOwners.filter(w => 
            w.owner_name.toLowerCase().includes(term) ||
            w.owner_email.toLowerCase().includes(term) ||
            w.wallet_name.toLowerCase().includes(term) ||
            w.wallet_address.toLowerCase().includes(term)
          )
        }

        setWallets(filteredWallets)
        setTotalCount(count || 0)
      }
    } catch (error) {
      console.error('Error fetching wallets:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWallets()
  }, [currentPage, ownerTypeFilter, channelFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchWallets()
  }

  const handleToggleStatus = async (walletId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('user_wallets')
        .update({ is_enabled: !currentStatus })
        .eq('id', walletId)

      if (error) throw error

      // 刷新列表
      fetchWallets()
    } catch (error) {
      console.error('Error toggling wallet status:', error)
      alert('操作失败，请重试')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  const totalPages = Math.ceil(totalCount / limit)

  if (loading && wallets.length === 0) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="page-title">钱包管理</h1>
        <p className="page-subtitle">查看和管理所有用户和运营商的钱包</p>
      </div>

      {/* 搜索和筛选 */}
      <div className="card mb-6">
        <div className="card-body">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  搜索
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="姓名、邮箱、钱包名称或地址"
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  所有者类型
                </label>
                <select
                  value={ownerTypeFilter}
                  onChange={(e) => {
                    setOwnerTypeFilter(e.target.value as 'all' | 'user' | 'operator')
                    setCurrentPage(1)
                  }}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">全部</option>
                  <option value="user">用户</option>
                  <option value="operator">运营商</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  支付通道
                </label>
                <select
                  value={channelFilter}
                  onChange={(e) => {
                    setChannelFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">全部通道</option>
                  <option value="bank_transfer">银行转账</option>
                  <option value="okx">OKX</option>
                  <option value="alipay">支付宝</option>
                  <option value="wechat">微信</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full btn btn-secondary"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  筛选
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 钱包列表 */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  所有者
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  钱包信息
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  支付通道
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  创建时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {wallets.map((wallet) => (
                <tr key={wallet.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <WalletIcon className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {wallet.owner_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {wallet.owner_email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      wallet.owner_type === 'user' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {wallet.owner_type === 'user' ? '用户' : '运营商'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="font-medium">{wallet.wallet_name}</div>
                      <div className="text-gray-500 font-mono text-xs mt-1 break-all max-w-xs">
                        {wallet.channel_code === 'bank_transfer' && wallet.bank_name && (
                          <div>{wallet.bank_name} - {wallet.account_holder_name}</div>
                        )}
                        {wallet.wallet_address}
                      </div>
                      {wallet.network && (
                        <div className="text-orange-600 text-xs mt-1">
                          网络: {wallet.network}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="mr-2">{wallet.channel_icon}</span>
                      <span className="text-sm text-gray-900">{wallet.channel_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {wallet.is_enabled ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          启用
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-3 w-3 mr-1" />
                          禁用
                        </span>
                      )}
                      {wallet.is_default && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          默认
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(wallet.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleToggleStatus(wallet.id, wallet.is_enabled)}
                      className={`${
                        wallet.is_enabled 
                          ? 'text-red-600 hover:text-red-900' 
                          : 'text-green-600 hover:text-green-900'
                      }`}
                    >
                      {wallet.is_enabled ? '禁用' : '启用'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {wallets.length === 0 && !loading && (
            <div className="text-center py-12">
              <WalletIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">暂无钱包记录</p>
            </div>
          )}
        </div>

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(currentPage - 1) * limit + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * limit, totalCount)}
                  </span>{' '}
                  条，共 <span className="font-medium">{totalCount}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
